import { useState, useEffect } from 'react';
import { CopyIcon, Wallet } from 'lucide-react';
import { walletAPI, referralAPI, settingsAPI } from '@/services/api';
import { toast } from 'react-toastify';
import Input from '@/components/Input';
import Button from '@/components/Button';
import Modal from '@/components/Modal';

const WalletModal = ({
  isOpen,
  onClose,
  onDiscountAmountChange,
  onApplyPromoCode,
  maxDiscountAmount = 0,
  userEmail: initialEmail = '',
  userPhone: initialPhone = ''
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [phone, setPhone] = useState(initialPhone);
  const [step, setStep] = useState('input'); // 'input' | 'otp' | 'balance'
  const [referralCode, setReferralCode] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [balance, setBalance] = useState(null);
  const [sponsorInfo, setSponsorInfo] = useState(null);
  const [discountAmount, setDiscountAmount] = useState('');
  const [verificationMode, setVerificationMode] = useState('OR');
  const [reorderDiscounts, setReorderDiscounts] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [otpPhoneNumber, setOtpPhoneNumber] = useState('');

  useEffect(() => {
    if (initialEmail && initialPhone) {
      setEmail(initialEmail);
      setPhone(initialPhone);
    }
  }, [initialEmail, initialPhone]);

  useEffect(() => {
    const fetchVerificationMode = async () => {
      try {
        const response = await settingsAPI.getReferralSettings();
        const mode = response.data?.referral_verification_mode || 'OR';
        setVerificationMode(mode);
      } catch (error) {
        console.error('Error fetching verification mode:', error);
        setVerificationMode('OR');
      }
    };

    if (isOpen) {
      fetchVerificationMode();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      handleReset(false);
    }
  }, [isOpen]);

  const handleVerifyIdentity = async () => {
    const hasEmail = email.trim().length > 0;
    const hasPhone = phone.trim().length > 0;

    if (verificationMode === 'AND') {
      if (!hasEmail || !hasPhone) {
        toast.error('Veuillez remplir l\'email ET le téléphone');
        return;
      }
    } else {
      if (!hasEmail && !hasPhone) {
        toast.error('Veuillez remplir l\'email OU le téléphone');
        return;
      }
    }

    setIsLoading(true);
    try {
      const userEmail = email.trim().toLowerCase() || '';
      const userPhone = phone.trim() || '';
      
      const identifierForCode = userEmail || userPhone;
      const codeResponse = await referralAPI.getCodeByEmail(identifierForCode).catch(() => ({ data: { code: null } }));
      const code = codeResponse.data.code;

      setReferralCode(code);

      if (!code) {
        const walletData = await fetchWalletData();
        if (!walletData.hasWallet) {
          const errorMsg = walletData.error || 'Aucun wallet trouvé avec ces informations. Vérifiez votre email et téléphone.';
          toast.error(errorMsg);
          setIsLoading(false);
          return;
        }
        const otpResponse = await walletAPI.sendOTP(null, userEmail, userPhone);
        const phoneFromResponse = otpResponse.data?.phone || userPhone;
        setOtpPhoneNumber(phoneFromResponse);
        setOtpSent(true);
        setStep('otp');
        toast.success('Code OTP envoyé par SMS');
        return;
      }

      const otpResponse = await walletAPI.sendOTP(code, userEmail, userPhone);
      const phoneFromResponse = otpResponse.data?.phone || userPhone;
      setOtpPhoneNumber(phoneFromResponse);
      setOtpSent(true);
      setStep('otp');
      toast.success('Code OTP envoyé par SMS');
    } catch (error) {
      const errorMsg = error.response?.data?.errors?.[0]?.msg || 'Erreur lors de la vérification';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp.trim() || otp.length !== 6) {
      toast.error('Veuillez entrer un code OTP valide (6 chiffres)');
      return;
    }

    setIsLoading(true);
    try {
      const response = await walletAPI.verifyOTP(referralCode, email.trim().toLowerCase() || '', phone.trim() || '', otp);

      if (response.data.verified) {
        const availableBalance = response.data.available_balance || 0;
        const transactionsData = response.data.transactions || [];
        const sponsorInfoData = response.data.sponsor || null;
        const referralCodeData = response.data.referral_code || null;
        const reorderDiscountsData = response.data.reorder_discounts || [];
        const userInfoData = response.data.user_info || null;
        
        setBalance(availableBalance);
        setTransactions(transactionsData);
        setSponsorInfo(sponsorInfoData);
        setReferralCode(referralCodeData);
        setReorderDiscounts(reorderDiscountsData);
        setUserInfo(userInfoData);
        
        if (availableBalance === 0 && transactionsData.length === 0) {
          toast.error('Aucun wallet trouvé avec ces informations. Vérifiez votre email et téléphone.');
          setStep('input');
          setIsLoading(false);
          return;
        }

        setStep('balance');

        const initialAmount = Math.min(availableBalance, maxDiscountAmount || availableBalance);
        setDiscountAmount(initialAmount.toFixed(2));

        toast.success('Code OTP vérifié avec succès');
      } else {
        toast.error('Code OTP invalide ou expiré');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.errors?.[0]?.msg || 'Erreur lors de la vérification';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWalletData = async () => {
    try {
      const balanceResponse = await walletAPI.getBalanceByEmail(email.trim().toLowerCase() || '', phone);
      const availableBalance = balanceResponse.data.available_balance || 0;
      const transactionsData = balanceResponse.data.transactions || [];
      
      setTransactions(transactionsData);
      setBalance(availableBalance);
      setSponsorInfo(balanceResponse.data.sponsor || null);
      setReferralCode(balanceResponse.data.referral_code || null);
      setReorderDiscounts(balanceResponse.data.reorder_discounts || []);
      setUserInfo(balanceResponse.data.user_info || null);
      setUserInfo(balanceResponse.data.user_info || null);

      return { availableBalance, hasWallet: availableBalance > 0 || transactionsData.length > 0 };
    } catch (error) {
      console.error('Error fetching wallet data:', error);
      setTransactions([]);
      setBalance(0);
      setSponsorInfo(null);
      setReferralCode(null);
      setReorderDiscounts([]);
      return { availableBalance: 0, hasWallet: false };
    }
  };

  const handleReset = (resetDiscount = false) => {
    setStep('input');
    setOtpSent(false);
    setOtp('');
    setOtpPhoneNumber('');
    if (!initialEmail) setEmail('');
    if (!initialPhone) setPhone('');
    setReferralCode(null);
    setTransactions([]);
    setBalance(null);
    setSponsorInfo(null);
    setDiscountAmount('');
    if (resetDiscount && onDiscountAmountChange) {
onDiscountAmountChange(0);
    }
  };

  const handleDiscountAmountChange = (value) => {
    if (!balance) {
      return;
    }

    if (value === '' || value === null || value === undefined) {
      setDiscountAmount('');
      return;
    }

    const numValue = parseFloat(value);

    if (isNaN(numValue) || numValue < 0) {
      setDiscountAmount('0');
      return;
    }

    const maxAmount = Math.min(balance, maxDiscountAmount || balance);

    if (numValue > maxAmount) {
      setDiscountAmount(maxAmount.toFixed(2));
      toast.warning(`Le montant maximum est de ${maxAmount.toFixed(2)} €`);
      return;
    }

    setDiscountAmount(value);
  };

  const handleCopyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Code de parrainage copié dans le presse-papiers');
  };

  const handleApplyDiscount = () => {
    if (!discountAmount || discountAmount.trim() === '') {
      toast.error('Veuillez entrer un montant');
      return;
    }

    const amount = parseFloat(discountAmount);

    if (isNaN(amount) || amount <= 0) {
      toast.error('Veuillez entrer un montant valide (supérieur à 0)');
      return;
    }

    if (amount < 0) {
      toast.error('Le montant ne peut pas être négatif');
      return;
    }

    if (amount > balance) {
      toast.error('Le montant ne peut pas dépasser le solde disponible');
      return;
    }

    const maxAmount = Math.min(balance, maxDiscountAmount || balance);
    if (amount > maxAmount) {
      toast.error(`Le montant ne peut pas dépasser ${maxAmount.toFixed(2)} €`);
      return;
    }

    if (onDiscountAmountChange) {
      onDiscountAmountChange(amount);
    }
    toast.success(`Réduction de ${amount.toFixed(2)} € appliquée`);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const renderContent = () => {
    if (step === 'input') {
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-700 mb-4">
            Accéder à mon wallet
          </h3>
          <p className="text-sm text-gray-600 mb-6">
            {verificationMode === 'AND'
              ? 'Entrez votre email ET votre numéro de téléphone pour accéder à votre wallet.'
              : 'Entrez votre email OU votre numéro de téléphone pour accéder à votre wallet.'}
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email {verificationMode === 'OR' && '(optionnel)'}
              </label>
              <Input
                name="email"
                labelText="Email"
                handleChange={(e) => setEmail(e.target.value)}
                value={email}
                placeholder="votre.email@exemple.com"
                type="email"
                withDiv={false}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Téléphone {verificationMode === 'OR' && '(optionnel)'}
              </label>
              <Input
                name="phone_number"
                labelText="Téléphone"
                handleChange={(e) => setPhone(e.target.value)}
                value={phone}
                placeholder="0612345678"
                type="tel"
                pattern="\d{10}"
                withDiv={false}
              />
            </div>

            <Button
              handleClick={handleVerifyIdentity}
              disabled={!isLoading && (verificationMode === 'AND' ? (email.trim() && phone.trim()) : (email.trim() || phone.trim()))}
              text={isLoading ? 'Vérification...' : 'Vérifier mon identité'}
              extraClasses="w-full"
            />
          </div>
        </div>
      );
    }

    if (step === 'otp') {
      const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return '';
        const cleaned = phoneNumber.replace(/\D/g, '');
        if (cleaned.length === 10) {
          return cleaned.match(/.{1,2}/g).join(' ');
        }
        return phoneNumber;
      };

      const displayPhone = otpPhoneNumber || phone.trim();

      return (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-700 mb-4">
            Vérification OTP
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Entrez le code OTP reçu par SMS{displayPhone ? ` au: ${formatPhoneNumber(displayPhone)}` : ''}
          </p>

          <div className="space-y-4">
            <Input
              handleChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              value={otp}
              placeholder="000000"
              customClass="w-full rounded-md border border-gray-300 px-3 py-2 text-center text-2xl tracking-widest"
              withDiv={false}
              type="text"
              extraAttributes={{ maxLength: 6 }}
            />

            <Button
              handleClick={handleVerifyOTP}
              disabled={!(isLoading || otp.length !== 6)}
              text={isLoading ? 'Vérification...' : 'Vérifier le code OTP'}
              extraClasses="w-full"
            />

            <button
              type="button"
              onClick={() => {
                setStep('input');
                setOtpSent(false);
                setOtp('');
              }}
              className="text-sm text-gray-600 hover:text-gray-800 w-full text-center"
            >
              Retour
            </button>
          </div>
        </div>
      );
    }

    if (step === 'balance' && balance !== null) {
      const welcomeName = userInfo 
        ? `${userInfo.firstname || ''} ${userInfo.lastname || ''}`.trim() || userInfo.email || userInfo.phone || 'Utilisateur'
        : email || phone || 'Utilisateur';
      const welcomeEmail = userInfo?.email || email || '';
      const welcomePhone = userInfo?.phone || phone || '';
      const welcomeText = userInfo 
        ? `${welcomeName}${welcomeEmail ? ` - ${welcomeEmail}` : ''}${welcomePhone ? ` - ${welcomePhone}` : ''}`
        : `${email || ''}${phone ? ` - ${phone}` : ''}`;

      return (
        <div className="space-y-4">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-700 mb-2">Mon Wallet</h3>
            <p className="text-sm text-gray-600">Bienvenue {welcomeText}</p>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
            <p className="text-sm text-gray-700 mb-2">Solde disponible :</p>
            <p className="text-2xl font-bold text-green-600">{balance.toFixed(2)} €</p>
          </div>

          {referralCode && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
              <p className="text-xs font-semibold text-yellow-800 mb-1">Votre code de parrainage :</p>
              <p className="text-lg font-bold text-yellow-800 flex items-center gap-2">
                {referralCode}
                <CopyIcon
                  className="w-4 h-4 cursor-pointer hover:text-yellow-600"
                  onClick={handleCopyReferralCode}
                />
              </p>
            </div>
          )}

          {reorderDiscounts.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Codes de réduction disponibles</h4>
              <div className="overflow-y-auto max-h-32 space-y-2 pr-2">
                {reorderDiscounts.map((discount) => (
                  <div
                    key={discount.id}
                    className="flex justify-between items-center p-2 border border-blue-200 rounded bg-blue-50"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{discount.discount_code}</p>
                      <p className="text-xs text-gray-500">
                        Valide jusqu'au {new Date(discount.eligible_until).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-bold text-blue-600">
                        -{discount.discount_amount.toFixed(2)} €
                      </div>
                      {onApplyPromoCode && (
                        <button
                          type="button"
                          onClick={() => {
                            if (onApplyPromoCode) {
                              onApplyPromoCode(discount.discount_code, email.trim().toLowerCase() || '', phone.trim() || '');
                              onClose();
                            }
                          }}
                          className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                        >
                          Appliquer
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Montant de réduction à appliquer (max: {Math.min(balance, maxDiscountAmount || balance).toFixed(2)} €)
            </label>
            <Input
              handleChange={(e) => handleDiscountAmountChange(e.target.value)}
              value={discountAmount}
              placeholder="0.00"
              type="number"
              min="0"
              step="0.01"
              max={Math.min(balance, maxDiscountAmount || balance)}
              customClass="rounded-md border border-gray-300 px-3 py-2 w-full"
              withDiv={false}
            />
            <p className="text-xs text-gray-500 mt-1">
              Vous pouvez utiliser jusqu'à {Math.min(balance, maxDiscountAmount || balance).toFixed(2)} €
            </p>
          </div>

          <Button
            handleClick={handleApplyDiscount}
            disabled={discountAmount && !isNaN(parseFloat(discountAmount)) && parseFloat(discountAmount) > 0 && parseFloat(discountAmount) <= Math.min(balance, maxDiscountAmount || balance)}
            text="Appliquer la réduction"
            extraClasses="w-full mb-4"
          />

          {transactions.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Historique récent</h4>
              <div className="overflow-y-auto max-h-48 space-y-2 pr-2">
                {transactions.slice(0, 5).map((transaction) => (
                  <div
                    key={transaction.id}
                    className="flex justify-between items-start p-2 border border-gray-200 rounded text-xs"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{transaction.description}</p>
                      <p className="text-gray-500">
                        {new Date(transaction.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short'
                        })}
                      </p>
                    </div>
                    <div className={`font-bold ${transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                      {transaction.type === 'credit' ? '+' : '-'}{transaction.amount.toFixed(2)} €
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={handleReset}
            className="text-sm text-gray-600 hover:text-gray-800 w-full text-center"
          >
            Utiliser un autre email/téléphone
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <Modal
      setShowModal={onClose}
      type="primary"
      title="Utiliser mon portefeuille parrainage"
      handleCloseModal={onClose}
      showFooterButtons={false}
      maxWidth="max-w-2xl"
      showModal={isOpen}
    >
      <div className="text-left max-h-[70vh] overflow-y-auto">
        {renderContent()}
      </div>
    </Modal>
  );
};

export default WalletModal;

